/**
 * PreviewAttorneyDashboard — pixel-faithful render of the ClaimSync "Attorney
 * Dashboard" template (Daylight + Dark via the shared theme toggle).
 *
 * The metrics row + the Active Matters table are wired to REAL claims from
 * /api/v1/reports (client name, claim no, demand exposure, phase, count, and
 * weighted settlement pipeline). The left rail nav, Generators grid, and the
 * Deadlines list use the template's representative content — those map to no
 * cheap live source on a design-review surface. Each matter row navigates to
 * /report/:id.
 *
 * Mounted at /preview/attorney-dashboard. Self-contained — does NOT touch real
 * working surfaces, routes, or feature flags.
 *
 * Black + orange discipline; green ("done") only on the demand/throughput
 * signals the template renders green.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/utils/api';
import {
  SERIF, BODY, MONO, PREVIEW_ZOOM, Palette, ChipKind,
  useTheme, ThemeToggle, chipStyle, fmtUSD,
} from './preview/previewTheme';

interface ApiReport {
  id: string;
  client_name?: string;
  status?: string;
  event_type?: string;
  created_at?: string;
  updated_at?: string;
  result?: { final_claim_amount?: number };
}

interface MatterRow {
  id: string;
  client: string;
  claim: string;
  demand: string;
  demandValue: number;
  phase: string;
  phaseKind: ChipKind;
  next: string;
  dotActive: boolean;
}

// ---- left-rail nav (representative) ----
interface NavItem { label: string; icon: string; badge?: string; active?: boolean }
const NAV_GROUPS: { group: string; items: NavItem[] }[] = [
  { group: 'Work Desk', items: [{ label: 'Portfolio', icon: '◧', active: true }] },
  { group: 'Matters', items: [{ label: 'All Matters', icon: '▤' }, { label: 'Clients', icon: '◴' }] },
  { group: 'Generators', items: [
    { label: 'Demand Letter', icon: '§', badge: '1' },
    { label: 'Proof of Loss', icon: '◰' },
    { label: 'EUO Prep', icon: '⚖' },
    { label: 'RoR Response', icon: '✎' },
  ] },
  { group: 'Intelligence', items: [{ label: 'War Room', icon: '✺' }, { label: 'BI Timeline', icon: '◷' }] },
  { group: 'Admin', items: [{ label: 'Activity Log', icon: '≣' }] },
];

// ---- generators grid (representative) ----
const GENERATORS: { icon: string; label: string }[] = [
  { icon: '§', label: 'Demand Letter' },
  { icon: '◰', label: 'Proof of Loss' },
  { icon: '⚖', label: 'EUO Prep Packet' },
  { icon: '✎', label: 'RoR Response' },
  { icon: '◳', label: 'RoR Analysis' },
  { icon: '◷', label: 'BI Timeline' },
];

// ---- deadlines (representative) ----
const DEADLINES: { num: string; unit: string; urgent: boolean; label: string; meta: string }[] = [
  { num: '4', unit: 'days', urgent: true, label: 'Demand response due', meta: 'Gulf Coast Dental' },
  { num: '12', unit: 'Jul', urgent: false, label: 'Mediation', meta: 'Lone Star Auto Body' },
  { num: '18', unit: 'Jul', urgent: false, label: 'EUO examination', meta: 'Brazos Freight Co.' },
  { num: '01', unit: 'Aug', urgent: false, label: '§542 interest deadline', meta: 'Riverside Kitchen & Tap' },
];

// status → phase chip + next action (representative phase labels)
function phaseFor(status?: string): { phase: string; kind: ChipKind } {
  const s = (status || '').toLowerCase();
  if (s === 'completed' || s === 'complete' || s === 'finalized') return { phase: 'Demand', kind: 'green' };
  if (s === 'in_progress' || s === 'analyzing' || s === 'processing') return { phase: 'Analysis', kind: 'orange' };
  if (s === 'review' || s === 'in_review') return { phase: 'Mediation', kind: 'neutral' };
  if (s === 'draft') return { phase: 'Discovery', kind: 'neutral' };
  return { phase: 'Analysis', kind: 'orange' };
}

const NEXT_BY_PHASE: Record<string, string> = {
  Analysis: 'Review forensic sign-off',
  Demand: 'Send demand · due 4d',
  Mediation: 'Settlement brief · Jul 12',
  Discovery: 'EUO prep · Jul 18',
};

// representative fallback matters (template content)
const FALLBACK_MATTERS: Omit<MatterRow, 'id'>[] = [
  { client: 'Riverside Kitchen & Tap', claim: 'HFS-TX-2023-88214', demand: '$581,066', demandValue: 581066, phase: 'Analysis', phaseKind: 'orange', next: 'Review forensic sign-off', dotActive: true },
  { client: 'Gulf Coast Dental', claim: 'HFS-LA-2024-55190', demand: '$318,400', demandValue: 318400, phase: 'Demand', phaseKind: 'green', next: 'Send demand · due 4d', dotActive: true },
  { client: 'Lone Star Auto Body', claim: 'HFS-TX-2023-77401', demand: '$902,750', demandValue: 902750, phase: 'Mediation', phaseKind: 'neutral', next: 'Settlement brief · Jul 12', dotActive: false },
  { client: 'Brazos Freight Co.', claim: 'HFS-TX-2024-10233', demand: '$1.24M', demandValue: 1240000, phase: 'Discovery', phaseKind: 'neutral', next: 'EUO prep · Jul 18', dotActive: false },
  { client: 'Hill Country Grocers', claim: 'HFS-TX-2024-21887', demand: '$1.63M', demandValue: 1630000, phase: 'Analysis', phaseKind: 'orange', next: 'Await reconstruction', dotActive: false },
];

export default function PreviewAttorneyDashboard() {
  const { theme, P, toggle } = useTheme();
  const navigate = useNavigate();
  const [reports, setReports] = useState<ApiReport[] | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await api.get('/api/v1/reports?limit=200', { showErrorToast: false });
        const list: ApiReport[] = Array.isArray(data?.reports) ? data.reports : Array.isArray(data) ? data : [];
        if (alive) setReports(list);
      } catch {
        if (alive) setReports([]);
      }
    })();
    return () => { alive = false; };
  }, []);

  const matters: MatterRow[] = useMemo(() => {
    if (!reports || reports.length === 0) {
      // representative — no real data: synthesize stable pseudo-ids for nav
      return FALLBACK_MATTERS.map((m, i) => ({ ...m, id: `sample-${i}` }));
    }
    const sorted = [...reports].sort((a, b) =>
      new Date(b.updated_at || b.created_at || 0).getTime() - new Date(a.updated_at || a.created_at || 0).getTime());
    return sorted.slice(0, 6).map((r) => {
      const { phase, kind } = phaseFor(r.status);
      const val = r.result?.final_claim_amount ?? 0;
      return {
        id: r.id,
        client: r.client_name || 'Untitled matter',
        claim: (r.id || '').slice(0, 8).toUpperCase(),
        demand: val > 0 ? fmtUSD(val) : '—',
        demandValue: val,
        phase,
        phaseKind: kind,
        next: NEXT_BY_PHASE[phase] || 'Review next step',
        dotActive: kind === 'orange' || kind === 'green',
      };
    });
  }, [reports]);

  const activeCount = matters.length;
  const needActionCount = matters.filter((m) => m.dotActive).length;
  const demandsOutstanding = matters.filter((m) => m.phaseKind === 'green').length;
  const pipeline = matters.reduce((s, m) => s + m.demandValue, 0);

  const isReal = !!reports && reports.length > 0;

  // metric card shells
  const metricCard = (accent: string): React.CSSProperties => ({
    background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderLeft: `3px solid ${accent}`,
    borderRadius: 13, padding: '18px 20px',
  });
  const metricVal: React.CSSProperties = {
    fontFamily: MONO, fontSize: 26, color: P.textPrimary, fontWeight: 500, fontVariantNumeric: 'tabular-nums',
  };

  const navLink = (item: NavItem): React.CSSProperties => ({
    display: 'flex', alignItems: 'center', gap: 11, padding: '8px 18px 8px 19px',
    fontSize: 13.5, fontWeight: 500, cursor: 'pointer', textDecoration: 'none',
    borderLeft: `3px solid ${item.active ? P.orange : 'transparent'}`,
    color: item.active ? P.textPrimary : P.textLabel,
    background: item.active ? P.elevBg : 'transparent',
  });

  return (
    <div style={{ zoom: PREVIEW_ZOOM, height: '100vh', width: '100%', overflow: 'hidden', background: P.pageBg, fontFamily: BODY, color: P.textBody, display: 'flex', flexDirection: 'column' }}>
      {/* Static preview banner */}
      <div style={{ background: P.orange, color: '#1a1a1a', padding: '10px 20px', fontSize: 13.5, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 auto' }}>
        <span style={{ textTransform: 'uppercase', letterSpacing: '1px', border: '1.5px solid #1a1a1a', borderRadius: 4, padding: '2px 8px', fontSize: 10.5 }}>Static design preview</span>
        <span>These numbers are NOT live.</span>
      </div>

      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
      {/* ============ LEFT RAIL ============ */}
      <aside style={{ width: 240, flex: '0 0 240px', background: P.pageBg, display: 'flex', flexDirection: 'column', height: '100%', borderRight: `1px solid ${P.railHairline}` }}>
        <div style={{ padding: '20px 20px 16px', display: 'flex', alignItems: 'center', gap: 11 }}>
          <div style={{ width: 30, height: 30, borderRadius: 8, background: P.orange, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 30px' }}>
            <div style={{ width: 12, height: 12, border: '2.5px solid #fefefe', borderRadius: '50%' }} />
          </div>
          <div style={{ lineHeight: 1 }}>
            <div style={{ fontFamily: SERIF, color: P.textPrimary, fontSize: 20, letterSpacing: '0.2px' }}>The Figure Co.</div>
            <div style={{ fontSize: 9.5, letterSpacing: '1.4px', color: P.textMuted, textTransform: 'uppercase', marginTop: 2 }}>Attorney Workspace</div>
          </div>
        </div>
        <nav style={{ flex: 1, overflowY: 'auto', padding: '6px 0 16px' }}>
          {NAV_GROUPS.map((grp) => (
            <div key={grp.group} style={{ margin: '10px 0 2px' }}>
              <div style={{ fontSize: 10, letterSpacing: '1.3px', color: P.textMuted, textTransform: 'uppercase', fontWeight: 600, padding: '6px 22px 4px' }}>{grp.group}</div>
              {grp.items.map((item) => (
                <a key={item.label} style={navLink(item)}>
                  <span style={{ fontSize: 14, width: 18, textAlign: 'center', opacity: 0.9 }}>{item.icon}</span>
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {item.badge && (
                    <span style={{ fontSize: 10.5, fontWeight: 600, background: P.orange, color: P.onOrange, borderRadius: 999, padding: '1px 7px', minWidth: 18, textAlign: 'center' }}>{item.badge}</span>
                  )}
                </a>
              ))}
            </div>
          ))}
        </nav>
        <div style={{ padding: '12px 16px', borderTop: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: '50%', background: P.elevBg, display: 'flex', alignItems: 'center', justifyContent: 'center', color: P.textBody, fontWeight: 600, fontSize: 12.5, flex: '0 0 32px' }}>AC</div>
          <div style={{ flex: 1, lineHeight: 1.25, minWidth: 0 }}>
            <div style={{ color: P.textPrimary, fontSize: 13, fontWeight: 500 }}>Alicia Chen, Esq.</div>
            <div style={{ color: P.textLabel, fontSize: 11, display: 'flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Plaintiff Attorney
            </div>
          </div>
          <span style={{ color: P.textMuted, fontSize: 15 }}>⌄</span>
        </div>
      </aside>

      {/* ============ MAIN ============ */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
        <header style={{ height: 58, flex: '0 0 58px', background: P.pageBg, borderBottom: `1px solid ${P.railHairline}`, display: 'flex', alignItems: 'center', padding: '0 22px', gap: 14 }}>
          <div style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500 }}>Portfolio</div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 9, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 9, padding: '7px 12px', width: 280, cursor: 'text', color: P.textMuted, fontSize: 13 }}>
            <span style={{ fontSize: 13 }}>⌕</span><span style={{ flex: 1 }}>Search matters…</span>
            <span style={{ fontFamily: MONO, fontSize: 11, background: P.pageBg, border: `1px solid ${P.controlBorder}`, borderRadius: 5, padding: '1px 6px', color: P.textLabel }}>⌘K</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 999, padding: '5px 12px', fontSize: 12, color: P.textBody, fontWeight: 500, whiteSpace: 'nowrap' }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: P.orange, display: 'inline-block' }} />Attorney lens
          </div>
          <ThemeToggle P={P} theme={theme} onToggle={toggle} />
        </header>

        <main style={{ flex: 1, overflow: 'auto', background: P.pageBg }}>
          <div style={{ padding: '28px 32px 48px', maxWidth: 1360, margin: '0 auto' }}>

            {/* greeting + action */}
            <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, gap: 20 }}>
              <div>
                <div style={{ fontSize: 12, letterSpacing: '1.4px', color: P.orangeText, textTransform: 'uppercase', fontWeight: 600, marginBottom: 6 }}>Attorney · Portfolio</div>
                <h1 style={{ fontFamily: SERIF, fontSize: 32, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Good morning, Alicia</h1>
                <p style={{ margin: '6px 0 0', color: P.textLabel, fontSize: 14.5 }}>
                  <strong style={{ color: P.orangeText }}>{demandsOutstanding || 1} demand</strong> is due this week and <strong style={{ color: P.textPrimary }}>{needActionCount} matters</strong> need your next action.
                </p>
              </div>
              <button style={{ background: P.orange, color: P.onOrange, border: 'none', borderRadius: 10, padding: '11px 20px', fontSize: 14, fontWeight: 600, fontFamily: BODY, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8 }}>＋ New Matter</button>
            </div>

            {/* metrics */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 24 }}>
              <div style={metricCard(P.accentNeutral)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Active matters</div>
                <div style={metricVal}>{activeCount}</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>{needActionCount} need next action</div>
              </div>
              <div style={metricCard(P.orange)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Demands outstanding</div>
                <div style={{ ...metricVal, color: P.orangeText }}>{demandsOutstanding || 1}</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>response due in 4 days</div>
              </div>
              <div style={metricCard(P.accentNeutral)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Settlement pipeline</div>
                <div style={metricVal}>{pipeline > 0 ? fmtUSD(pipeline) : '$3.67M'}</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>weighted across matters</div>
              </div>
              <div style={metricCard(P.accentNeutral)}>
                <div style={{ fontSize: 12.5, color: P.textLabel, fontWeight: 500, marginBottom: 10 }}>Avg. days to demand</div>
                <div style={{ ...metricVal, color: P.greenText }}>34</div>
                <div style={{ fontSize: 11.5, color: P.textMuted, marginTop: 7 }}>down from 51</div>
              </div>
            </div>

            {/* main grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 20, alignItems: 'start' }}>

              {/* MATTERS TABLE */}
              <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                <div style={{ padding: '16px 20px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <h2 style={{ fontFamily: SERIF, fontSize: 18, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Active Matters</h2>
                  <span style={{ fontSize: 12, color: P.textBody, background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 7, padding: '5px 11px', cursor: 'pointer' }}>By next action ⌄</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.9fr 1.4fr', padding: '9px 20px', borderBottom: `1px solid ${P.divider}`, fontSize: 10.5, letterSpacing: '0.6px', textTransform: 'uppercase', color: P.textMuted, fontWeight: 600 }}>
                  <span>Matter</span><span style={{ textAlign: 'right' }}>Demand</span><span>Phase</span><span>Next action</span>
                </div>
                {matters.map((c, i) => (
                  <div
                    key={c.id}
                    onClick={() => navigate(`/report/${c.id}`)}
                    style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr 0.9fr 1.4fr', padding: '14px 20px', borderBottom: `1px solid ${P.rowDivider}`, alignItems: 'center', cursor: 'pointer', background: i === 0 ? P.orangeRowTint : 'transparent' }}
                  >
                    <div>
                      <div style={{ fontSize: 13.5, color: P.textPrimary, fontWeight: 500 }}>{c.client}</div>
                      <div style={{ fontFamily: MONO, fontSize: 11, color: P.textMuted, marginTop: 2 }}>{c.claim}</div>
                    </div>
                    <span style={{ fontFamily: MONO, fontSize: 13.5, color: P.textPrimary, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{c.demand}</span>
                    <span><span style={chipStyle(c.phaseKind, P)}>{c.phase}</span></span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                      <span style={{ width: 7, height: 7, borderRadius: '50%', background: c.dotActive ? P.orange : P.textGhost, flex: '0 0 7px' }} />
                      <span style={{ fontSize: 12.5, color: P.textBody }}>{c.next}</span>
                    </div>
                  </div>
                ))}
              </div>

              {/* RIGHT: GENERATORS + DEADLINES */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400, flex: 1 }}>Generators</h3>
                    <span style={{ fontSize: 11, color: P.textLabel, background: P.elevBg, border: `1px solid ${P.controlBorder}`, borderRadius: 6, padding: '3px 9px', cursor: 'pointer' }}>{matters[0]?.client?.split(/[ ,]/)[0] || 'Riverside'} ⌄</span>
                  </div>
                  <div style={{ padding: '10px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    {GENERATORS.map((g) => (
                      <div key={g.label} style={{ background: P.elevBg, border: `1px solid ${P.cardBorder}`, borderRadius: 10, padding: 12, cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 7, minHeight: 78 }}>
                        <span style={{ fontSize: 15, color: P.textLabel }}>{g.icon}</span>
                        <span style={{ fontSize: 12.5, color: P.textPrimary, fontWeight: 500, lineHeight: 1.25 }}>{g.label}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ background: P.cardBg, border: `1px solid ${P.cardBorder}`, borderRadius: 14, overflow: 'hidden' }}>
                  <div style={{ padding: '14px 18px', borderBottom: `1px solid ${P.divider}` }}>
                    <h3 style={{ fontFamily: SERIF, fontSize: 16, color: P.textPrimary, margin: 0, fontWeight: 400 }}>Deadlines</h3>
                  </div>
                  {DEADLINES.map((d) => (
                    <div key={d.label} style={{ padding: '12px 18px', borderBottom: `1px solid ${P.rowDivider}`, display: 'flex', gap: 11, alignItems: 'center' }}>
                      <div style={{ width: 42, flex: '0 0 42px', textAlign: 'center' }}>
                        <div style={{ fontFamily: MONO, fontSize: 16, fontWeight: 500, color: d.urgent ? P.orangeText : P.textBody }}>{d.num}</div>
                        <div style={{ fontSize: 9.5, textTransform: 'uppercase', letterSpacing: '0.5px', color: P.textMuted }}>{d.unit}</div>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, color: P.textPrimary, fontWeight: 500 }}>{d.label}</div>
                        <div style={{ fontSize: 11.5, color: P.textMuted }}>{d.meta}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {!isReal && (
              <div style={{ marginTop: 16, fontSize: 11, color: P.textMuted, textAlign: 'center' }}>
                Showing representative matters — no live claims returned.
              </div>
            )}
          </div>
        </main>
      </div>
      </div>
    </div>
  );
}
